import { isTeamCoOwnerActor, isTeamOwnerActor } from './teamConfig';
import {
  CONDITION_OPERATOR_OPTIONS,
  CONFIRMATION_REQUIREMENT_OPTIONS,
  EVIDENCE_REQUIREMENT_OPTIONS,
  REASON_REQUIREMENT_OPTIONS,
  applyCaseActionDerivedFields,
  buildCaseActionScopeKey,
  findConflictingCaseActionRule,
  findDuplicateCaseActionRule,
  findOverlappingCaseActionRule,
  formatConditionLine,
  getActionTargetLabel,
  getActionTypeLabel,
  getActionTypesForModuleRecord,
  getCaseActionModuleLabel,
  getConditionFieldsForSource,
  getConditionSourcesForScope,
  getConditionValueOptions,
  getConfirmationRequirementLabel,
  getEvidenceRequirementLabel,
  getEvidenceSourceLabel,
  getEvidenceSourceReference,
  getEvidenceSourcesForScope,
  getReasonRequirementLabel,
  getRecordTypeLabel,
  getRecordTypeOptions,
  getTargetResolutionsForRule,
  isEvidenceSourceAvailable,
  nextCaseActionRuleId,
  normalizeCaseActionForm,
  requiresConfirmationMessage,
  requiresEvidenceSource,
  operatorIsMulti,
  supportsDecisionOutcome,
} from './caseActionsConfig';
import { emitRuleSaveHistory } from './changeHistoryEmitters';

const ACTOR = {
  name: 'Olivia Brown',
  role: 'Configuration Admin',
  team: 'Platform Operations',
  accountId: 'ADM-0002',
};

export function caseActionRuleActor() {
  return { ...ACTOR };
}

export function canAccessCaseActions(auth) {
  return isTeamOwnerActor(auth) || isTeamCoOwnerActor(auth);
}

export function canViewCaseActions(auth) {
  return canAccessCaseActions(auth);
}

export function canAddCaseActionRule(auth) {
  return canAccessCaseActions(auth);
}

export function canEditCaseActionRule(auth) {
  return canAccessCaseActions(auth);
}

export function canSaveCaseActionRule(auth) {
  return canAccessCaseActions(auth);
}

function addError(result, key, message) {
  if (!result.summary.includes(message)) result.summary.push(message);
  if (key && !result.errors[key]) result.errors[key] = message;
}

function conditionValues(condition) {
  return [...new Set((condition?.values || []).map((value) => String(value || '').trim()).filter(Boolean))];
}

function conditionFingerprint(condition) {
  return [
    condition.source || '',
    condition.field || '',
    condition.operator || '',
    conditionValues(condition).slice().sort().join(','),
  ].join('|');
}

function conditionsCannotAllBeTrue(conditions = []) {
  const byField = {};
  conditions.forEach((condition) => {
    if (!condition.source || !condition.field || !condition.operator) return;
    const values = conditionValues(condition);
    if (!values.length) return;
    const key = `${condition.source}|${condition.field}`;
    if (!byField[key]) byField[key] = [];
    byField[key].push({ operator: condition.operator, values });
  });

  return Object.values(byField).some((entries) => {
    const equals = [...new Set(entries.filter((entry) => entry.operator === 'equals').flatMap((entry) => entry.values))];
    if (equals.length > 1) return true;
    const uniquePositives = equals;
    return entries.some((entry) => {
      if (entry.operator === 'does_not_equal' && uniquePositives.includes(entry.values[0])) return true;
      if (entry.operator === 'is_not_any_of' && uniquePositives.length && uniquePositives.every((value) => entry.values.includes(value))) {
        return true;
      }
      if (entry.operator === 'is_any_of' && equals.length && !entry.values.some((value) => equals.includes(value))) return true;
      return false;
    });
  });
}

export function collectCaseActionSaveErrors(
  formData,
  allRules,
  excludeId,
  { originalLastUpdated, originalScopeKey, canSave = true, checksAvailable = true } = {},
) {
  const form = applyCaseActionDerivedFields(normalizeCaseActionForm(formData));
  const result = { errors: {}, summary: [] };
  const others = (allRules || []).filter((rule) => rule.id !== excludeId).map(normalizeCaseActionForm);

  if (!checksAvailable) {
    addError(result, 'checksUnavailable', 'Required checks could not be completed. Try again.');
    return result;
  }
  if (!canSave) {
    addError(result, 'permission', 'You do not have permission to save this Rule.');
    return result;
  }

  if (!String(form.ruleName || '').trim()) addError(result, 'ruleName', 'Rule Name is required.');

  if (!form.module) addError(result, 'module', 'Module is required.');

  const recordTypes = getRecordTypeOptions(form.module);
  if (!form.recordType) addError(result, 'recordType', 'Record Type is required.');
  else if (form.module && !recordTypes.some((option) => option.value === form.recordType)) {
    addError(result, 'recordType', 'Select a compatible Record Type.');
  }

  const actionTypes = getActionTypesForModuleRecord(form.module, form.recordType);
  if (!form.actionType) addError(result, 'actionType', 'Action Type is required.');
  else if (form.module && form.recordType && !actionTypes.some((option) => option.value === form.actionType)) {
    addError(result, 'actionType', 'Select a compatible Action Type.');
  }

  const targets = getTargetResolutionsForRule(form.module, form.recordType);
  if (!form.actionTarget) addError(result, 'actionTarget', 'Action Target is required.');
  else if (form.module && form.recordType && targets.length && !targets.some((option) => option.value === form.actionTarget)) {
    addError(result, 'actionTarget', 'Select a compatible Action Target.');
  }

  if (!REASON_REQUIREMENT_OPTIONS.some((option) => option.value === form.reasonRequirement)) {
    addError(result, 'reasonRequirement', 'Reason Requirement is required.');
  }
  if (!EVIDENCE_REQUIREMENT_OPTIONS.some((option) => option.value === form.evidenceRequirement)) {
    addError(result, 'evidenceRequirement', 'Evidence Requirement is required.');
  }
  if (requiresEvidenceSource(form.evidenceRequirement)) {
    if (!form.evidenceSource) addError(result, 'evidenceSource', 'Select an Evidence Source.');
    else if (!isEvidenceSourceAvailable(form.evidenceSource)) {
      addError(result, 'evidenceSource', 'This selection is unavailable or incompatible.');
    } else if (!getEvidenceSourcesForScope().some((option) => option.value === form.evidenceSource)) {
      addError(result, 'evidenceSource', 'Select a compatible Evidence Source.');
    }
  }
  if (!CONFIRMATION_REQUIREMENT_OPTIONS.some((option) => option.value === form.confirmationRequirement)) {
    addError(result, 'confirmationRequirement', 'Confirmation Requirement is required.');
  }
  if (requiresConfirmationMessage(form.confirmationRequirement) && !String(form.confirmationMessage || '').trim()) {
    addError(result, 'confirmationMessage', 'Enter a Confirmation Message.');
  }

  if (!String(form.changeReason || '').trim()) addError(result, 'changeReason', 'Enter a Change Reason.');

  if (excludeId && originalScopeKey && buildCaseActionScopeKey(form) !== originalScopeKey) {
    addError(result, 'fixedScope', 'These scope fields cannot be changed.');
  }

  const sources = getConditionSourcesForScope(form.module).map((option) => option.value);
  const seenFingerprints = [];
  (form.conditions || []).forEach((condition, index) => {
    const key = `condition_${index}`;
    const values = conditionValues(condition);
    if (!condition.source || !condition.field || !condition.operator || !values.length) {
      addError(result, key, 'Complete or remove this Condition.');
      return;
    }
    if (!sources.includes(condition.source)) {
      addError(result, key, 'This selection is unavailable or incompatible.');
    }
    const fields = getConditionFieldsForSource(condition.source);
    if (!fields.some((option) => option.value === condition.field)) {
      addError(result, key, 'This selection is unavailable or incompatible.');
    }
    if (!CONDITION_OPERATOR_OPTIONS.some((option) => option.value === condition.operator)) {
      addError(result, key, 'This selection is unavailable or incompatible.');
    }
    if (!operatorIsMulti(condition.operator) && values.length !== 1) {
      addError(result, key, 'Complete or remove this Condition.');
    }
    const valueOptions = getConditionValueOptions(condition.source, condition.field).map((option) => option.value);
    if (values.some((value) => valueOptions.length && !valueOptions.includes(value))) {
      addError(result, key, 'This selection is unavailable or incompatible.');
    }
    if (values.length !== uniqueCount(values)) {
      addError(result, key, 'Remove repeated values.');
    }
    const fingerprint = conditionFingerprint(condition);
    if (seenFingerprints.includes(fingerprint)) addError(result, key, 'This Condition is already included.');
    seenFingerprints.push(fingerprint);
  });

  if (conditionsCannotAllBeTrue(form.conditions)) {
    addError(result, 'conditions', 'These Conditions cannot all be satisfied.');
  }

  const dup = findDuplicateCaseActionRule(others, form, excludeId);
  if (dup) addError(result, 'duplicate', `This Rule duplicates ${dup.id}.`);

  const overlap = findOverlappingCaseActionRule(others, form, excludeId);
  if (overlap && (!dup || overlap.id !== dup.id)) {
    if (!(form.conditions || []).length || !(overlap.conditions || []).length) {
      addError(result, 'overlap', `A Rule with No Conditions already covers this scope.`);
    } else {
      addError(result, 'overlap', `This Rule overlaps ${overlap.id}.`);
    }
  }

  const conflict = findConflictingCaseActionRule(others, form, excludeId);
  if (conflict && (!overlap || conflict.id !== overlap.id || !result.errors.overlap)) {
    addError(result, 'conflict', `This Rule conflicts with ${conflict.id}.`);
  }

  if (excludeId && originalLastUpdated) {
    const current = (allRules || []).find((rule) => rule.id === excludeId);
    if (current && (current.lastUpdated || current.modifiedOn) && (current.lastUpdated || current.modifiedOn) !== originalLastUpdated) {
      addError(result, 'concurrent', 'This Rule was updated by another Team Member. Review it before saving.');
    }
  }

  return result;
}

function uniqueCount(values = []) {
  return new Set(values).size;
}

export function ruleSnapshotFromForm(formData) {
  const form = applyCaseActionDerivedFields(normalizeCaseActionForm(formData));
  return {
    ruleName: String(form.ruleName || '').trim(),
    module: form.module,
    recordType: form.recordType,
    actionType: form.actionType,
    actionTarget: form.actionTarget,
    decisionOutcome: supportsDecisionOutcome(form.module, form.recordType)
      ? (form.decisionOutcome || '')
      : '',
    reasonRequirement: form.reasonRequirement,
    evidenceRequirement: form.evidenceRequirement,
    evidenceSource: requiresEvidenceSource(form.evidenceRequirement) ? form.evidenceSource : '',
    confirmationRequirement: form.confirmationRequirement,
    confirmationMessage: requiresConfirmationMessage(form.confirmationRequirement)
      ? String(form.confirmationMessage || '').trim()
      : '',
    conditions: (form.conditions || []).map((condition) => ({
      id: condition.id,
      source: condition.source,
      field: condition.field,
      operator: condition.operator,
      values: conditionValues(condition),
    })),
  };
}

export function getPublishedRule(rule) {
  if (!rule) return null;
  const { pendingDraft, ...published } = rule;
  return normalizeCaseActionForm(published);
}

export function getEditRuleSource(rule) {
  if (!rule) return null;
  return normalizeCaseActionForm(rule);
}

export function appendTestHistory(rule, summary, passed) {
  const actor = caseActionRuleActor();
  const base = Array.isArray(rule.testHistory) ? rule.testHistory : [];
  return [
    ...base,
    {
      actor: actor.name,
      role: actor.role,
      accountId: actor.accountId,
      timestamp: new Date().toISOString(),
      result: passed ? 'passed' : 'blocked',
      summary,
    },
  ];
}

function snapshotsEqual(a, b) {
  return JSON.stringify(a) === JSON.stringify(b);
}

export function mergeRulesAfterSave(allRules, payload, publishedBaseline) {
  const snapshot = ruleSnapshotFromForm(payload);
  const isNew = !publishedBaseline?.id;
  const nowIso = payload.lastUpdated || new Date().toISOString();
  const actor = payload.lastUpdatedBy || ACTOR.name;
  const id = payload.id || publishedBaseline?.id || nextCaseActionRuleId(allRules);

  if (!isNew) {
    const prevSnap = ruleSnapshotFromForm(publishedBaseline);
    if (snapshotsEqual(prevSnap, snapshot)) {
      return {
        rules: allRules,
        record: getPublishedRule(publishedBaseline),
        unchanged: true,
        isNew: false,
      };
    }
  }

  const record = {
    ...snapshot,
    id,
    dateAdded: isNew ? nowIso : publishedBaseline.dateAdded || publishedBaseline.createdOn || nowIso,
    addedBy: isNew ? actor : publishedBaseline.addedBy || publishedBaseline.createdBy || actor,
    lastUpdated: nowIso,
    lastUpdatedBy: actor,
    changeReason: String(payload.changeReason || '').trim(),
    testHistory: payload.testHistory || publishedBaseline?.testHistory || [],
    configurationHistory: payload.configurationHistory || publishedBaseline?.configurationHistory || [],
  };
  try {
    emitRuleSaveHistory({
      isNew,
      previous: publishedBaseline,
      next: record,
      module: 'Configuration — Case Actions',
      sourceModuleRef: 'configuration_case_actions',
      changedItem: record.ruleName || record.name || id,
      itemId: id,
      changeReason: record.changeReason,
      actor,
      stamp: nowIso,
      compareKeys: [
        'ruleName',
        'reasonRequirement',
        'evidenceRequirement',
        'evidenceSource',
        'confirmationRequirement',
        'confirmationMessage',
        'conditions',
        'decisionOutcome',
      ],
    });
  } catch {
    /* history recording failure must not reverse the source save */
  }

  const rules = isNew
    ? [...allRules.filter((rule) => rule.id !== record.id), record]
    : allRules.map((rule) => (rule.id === record.id ? { ...rule, ...record } : rule));

  return { rules, record, unchanged: false, isNew };
}

export function saveCaseActionRule(allRules, payload, publishedBaseline, options = {}) {
  const excludeId = publishedBaseline?.id || payload?.id || undefined;
  const validation = collectCaseActionSaveErrors(payload, allRules, excludeId, {
    originalLastUpdated: options.originalLastUpdated
      ?? publishedBaseline?.lastUpdated
      ?? publishedBaseline?.modifiedOn,
    originalScopeKey: options.originalScopeKey
      ?? (publishedBaseline ? buildCaseActionScopeKey(publishedBaseline) : undefined),
    canSave: options.canSave !== false,
    checksAvailable: options.checksAvailable !== false,
  });

  if (validation.summary.length) {
    return {
      success: false,
      errors: validation.errors,
      summary: validation.summary,
      rules: allRules,
      unchanged: false,
    };
  }

  try {
    const merged = mergeRulesAfterSave(allRules, payload, publishedBaseline || {});
    return {
      success: true,
      errors: {},
      summary: [],
      rules: merged.rules,
      record: merged.record,
      unchanged: Boolean(merged.unchanged),
      isNew: Boolean(merged.isNew),
    };
  } catch {
    return {
      success: false,
      errors: { form: 'The Rule could not be saved. Your changes have been kept for review.' },
      summary: ['The Rule could not be saved. Your changes have been kept for review.'],
      rules: allRules,
      unchanged: false,
    };
  }
}

export function mergeRulesAfterActivate(allRules, payload, publishedBaseline) {
  const result = saveCaseActionRule(allRules, {
    ...payload,
    changeReason: payload.changeReason || 'Save from configuration test',
  }, publishedBaseline || {}, { canSave: true, checksAvailable: true });
  return result.rules;
}

export function collectActivationErrors(formData, allRules, excludeId) {
  return collectCaseActionSaveErrors(formData, allRules, excludeId).summary;
}

export function runCaseActionRuleTest(formData, allRules) {
  const snapshot = ruleSnapshotFromForm(formData);
  const validation = collectCaseActionSaveErrors(formData, allRules, formData.id || undefined);
  const lines = [
    `Module: ${getCaseActionModuleLabel(snapshot.module) || '—'}`,
    `Record Type: ${getRecordTypeLabel(snapshot.module, snapshot.recordType) || '—'}`,
    `Action Type: ${getActionTypeLabel(snapshot.actionType) || '—'}`,
    `Action Target: ${getActionTargetLabel(snapshot.module, snapshot.recordType, snapshot.actionTarget) || '—'}`,
    `Reason Requirement: ${getReasonRequirementLabel(snapshot.reasonRequirement) || '—'}`,
    `Evidence Requirement: ${getEvidenceRequirementLabel(snapshot.evidenceRequirement) || '—'}`,
    `Evidence Source: ${requiresEvidenceSource(snapshot.evidenceRequirement) ? (getEvidenceSourceLabel(snapshot.evidenceSource) || '—') : 'Not Required'}`,
    `Evidence reference: ${getEvidenceSourceReference(snapshot.evidenceSource) || '—'}`,
    `Confirmation Requirement: ${getConfirmationRequirementLabel(snapshot.confirmationRequirement) || '—'}`,
  ];
  if ((snapshot.conditions || []).length) {
    lines.push('', 'Conditions:', ...snapshot.conditions.map((condition) => `• ${formatConditionLine(condition)}`));
  } else {
    lines.push('', 'No Conditions');
  }
  if (validation.summary.length) {
    return {
      passed: false,
      summary: 'Rule test blocked. Configuration conflicts or missing requirements were found.',
      detail: [...lines, '', 'Issues:', ...validation.summary.map((message) => `• ${message}`)].join('\n'),
    };
  }
  return {
    passed: true,
    summary: 'Rule test passed. Configuration is valid — no case action was performed.',
    detail: [...lines, '', 'No duplicate, overlapping or conflicting Rules detected.'].join('\n'),
  };
}

export function mergeRulesAfterSaveDraft(allRules, payload, publishedBaseline) {
  return mergeRulesAfterActivate(allRules, payload, publishedBaseline);
}

export function mergeRulesAfterInactivate(allRules) {
  return allRules;
}

export function mergeRulesAfterListActivate(allRules) {
  return allRules;
}
